import { useState } from 'react';
import { Loader2, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useRunTool } from '@/hooks/use-run-tool';
import { getApiErrorMessage } from '@/lib/utils';
import { missingInputs } from '@/modules/back-testing/datasets/tools/tool-call';
import { ToolCallForm } from '@/modules/back-testing/datasets/tools/tool-call-form';
import { toolAccess } from '@/modules/back-testing/datasets/tools/tool-filters';
import type { ToolDefinitionAction } from '@/services';
import { ConfirmWriteRun } from './confirm-write-run';
import { ToolRunResult } from './tool-run-result';

/**
 * The action's Test tab: fill in the tool's arguments, run it once on the live integration, read the answer.
 * A write tool asks for confirmation first.
 */
export const ToolTestSection = ({ action }: { action: ToolDefinitionAction }) => {
	const [args, setArgs] = useState<Record<string, unknown>>({});
	const [confirming, setConfirming] = useState(false);
	const run = useRunTool(action.provider, action.tool);
	const missing = missingInputs({ args }, action);
	const writes = toolAccess(action.riskLevel) === 'write';
	const send = () => run.mutate({ input: args });

	return (
		<div className='flex flex-col gap-4'>
			<div className='@container flex flex-col overflow-hidden rounded-xl border border-gray-500'>
				<div className='flex flex-col gap-0.5 px-4 py-3'>
					<p className='text-[13px] font-medium leading-4.5 text-text-heading'>Try it on your live integration</p>
					<p className='text-xs leading-4 text-gray-800'>
						{writes
							? 'Runs once with these arguments and changes data on your live integration.'
							: 'Runs once with these arguments. It only reads; nothing is changed.'}
					</p>
				</div>
				<ToolCallForm definition={action} sharedBy={[]} args={args} onChange={setArgs} />
				<div className='flex items-center justify-end gap-3 border-t border-gray-500 px-4 py-3'>
					{missing.length > 0 && <span className='text-xs text-red-600'>{`Fill in ${missing.join(', ')} to run.`}</span>}
					<Button
						size='sm'
						disabled={missing.length > 0 || run.isPending}
						onClick={() => (writes ? setConfirming(true) : send())}
					>
						{run.isPending ? <Loader2 className='size-3.5 animate-spin' /> : <Play className='size-3.5' />}
						Run
					</Button>
				</div>
			</div>
			{run.isError && (
				<p role='alert' className='text-[13px] leading-4.5 text-red-600'>
					{getApiErrorMessage(run.error, 'The run could not start.')}
				</p>
			)}
			{run.data && !run.isPending && <ToolRunResult result={run.data} writes={writes} />}
			<ConfirmWriteRun
				open={confirming}
				label={action.label}
				onOpenChange={setConfirming}
				onConfirm={send}
			/>
		</div>
	);
};
